/* Consulting Form Handler
 * Klaviyo Client Subscriptions API + Client Events API + GTM dataLayer
 *
 * Two Klaviyo API calls are made on every submission:
 *   1. Client Subscriptions API — saves phone_number reliably to the profile
 *   2. Client Events API        — fires the named metric to trigger the flow
 *
 * Works for new AND existing Klaviyo subscribers — identified by email,
 * no cookie dependency.
 *
 * Upload to: Shopify Admin → Themes → Edit code → Assets
 * Add to page template: <script src="{{ 'consulting-klaviyo-submission.js' | asset_url }}" defer></script>
 */
(function () {

  /* ─── ACCOUNT CONFIGURATION ───────────────────────────────────────────────
   * These are the only values that change between Klaviyo / GTM accounts.
   * ───────────────────────────────────────────────────────────────────────── */
  var CONFIG = {
    // Klaviyo public Company ID
    // Where to find: Klaviyo Admin → Settings → Account → API Keys → Public API Key (6-character code)
    klaviyoCompanyId: 'VcCjVx',

    // Klaviyo list ID for consulting leads
    // Used by the Subscriptions API to save the profile (including phone) to a list
    // Where to find: Klaviyo Admin → Lists → your list → ID shown in the URL
    klaviyoListId: 'R7zdY5',

    // The metric name that triggers your Klaviyo flow.
    // Must match EXACTLY — Klaviyo is case-sensitive.
    // Where to find: Klaviyo Admin → Flows → your flow → trigger metric name
    klaviyoEventName: 'Consulting Form Submitted',

    // GTM dataLayer event name.
    // Must match the "Event Name" field in your GTM Custom Event trigger.
    // Where to find: GTM → Triggers → your trigger → Event Name
    gtmEventName: 'consulting_form_submit',
  };
  /* ───────────────────────────────────────────────────────────────────────── */

  var LOG = '[consulting-form]';


  function isTrackingTest() { return window.GTDChatraCollectorConfig?.themeRole === 'unpublished' || window.GTDChatraCollectorConfig?.testMode === true; }
  async function sendKlaviyo(url, options) {
    const isEvent = JSON.parse(options.body).data.type === 'event';
    for (let attempt = 0; attempt < (isEvent ? 3 : 1); attempt++) {
      let response;
      const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 8000);
      try {
        response = await fetch(url, {...options, signal: controller.signal});
        if (response.ok) return response;
        if (response.status < 500 && ![408,425,429].includes(response.status)) throw new Error('provider_rejected');
      } catch (error) {
        if (error.message === 'provider_rejected' || !isEvent || attempt === 2) throw new Error('delivery_not_confirmed');
      } finally { clearTimeout(timer); }
      if (!isEvent || attempt === 2) throw new Error('delivery_not_confirmed');
      const retry = response?.headers?.get('Retry-After');
      const serverDelay = retry && /^\d+$/.test(retry) ? Number(retry)*1000 : Math.max(0, Date.parse(retry || '')-Date.now()) || 0;
      if (serverDelay > 15000) throw new Error('delivery_retry_later');
      await new Promise(resolve => setTimeout(resolve, Math.max(1000*2**attempt, serverDelay)));
    }
    throw new Error('delivery_not_confirmed');
  }
  function normalizePhone(phone) {
    var d = (phone || '').replace(/\D/g, '');
    if (!d) return null;                                   // empty — omit from payload
    if (d.length === 10)                 return '+1' + d;  // US 10-digit
    if (d.length === 11 && d[0] === '1') return '+' + d;  // US 11-digit with country code
    return '+' + d;                                        // international — best effort E.164
  }

  function splitName(full) {
    var p = (full || '').trim().split(/\s+/);
    return { first: p[0] || '', last: p.slice(1).join(' ') || '' };
  }

  async function sha256hex(str) {
    var buf = await crypto.subtle.digest(
      'SHA-256',
      new TextEncoder().encode((str || '').trim().toLowerCase())
    );
    return Array.from(new Uint8Array(buf))
      .map(function (b) { return b.toString(16).padStart(2, '0'); })
      .join('');
  }

  function buildProfileAttrs(data) {
    var n     = splitName(data.name);
    var phone = normalizePhone(data.phone);
    var attrs = {
      email:        data.email,
      first_name:   n.first,
      last_name:    n.last,
      organization: data.company,
    };
    if (phone) attrs.phone_number = phone;
    attrs.properties = Object.assign({}, attrs.properties || {}, data.tracking || {});
    return attrs;
  }

  async function subscribeToList(data) {
    var profileAttrs = buildProfileAttrs(data);
    var res = await sendKlaviyo(
      'https://a.klaviyo.com/client/subscriptions/?company_id=' + CONFIG.klaviyoCompanyId,
      {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', revision: '2026-07-15' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              profile: {
                data: {
                  type:       'profile',
                  attributes: profileAttrs,
                }
              }
            },
            relationships: {
              list: { data: { type: 'list', id: CONFIG.klaviyoListId } }
            }
          }
        }),
      }
    );
    return res;
  }

  async function submitToKlaviyo(data) {
    var profileAttrs = buildProfileAttrs(data);
    var res = await sendKlaviyo(
      'https://a.klaviyo.com/client/events/?company_id=' + CONFIG.klaviyoCompanyId,
      {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', revision: '2026-07-15' },
        body: JSON.stringify({
          data: {
            type: 'event',
            attributes: {
              metric: {
                data: {
                  type:       'metric',
                  attributes: { name: CONFIG.klaviyoEventName + (isTrackingTest() ? ' TEST' : '') }
                }
              },
              profile: {
                data: {
                  type:       'profile',
                  attributes: profileAttrs,
                }
              },
              unique_id: data.deliveryId,
              properties: {
                ...(data.tracking || {}),
                gtd_source_form_at: data.submittedAt,
                gtd_browser_time_basis: 'device_clock',
                gtd_tracking_test: isTrackingTest(),
                facility_type:       data.facilityType,
                project_stage:       data.projectStage,
                estimated_grow_size: data.growSize,
                phone:               data.phone,

                page_name:           data.pageName,
                page_url:            data.pageUrl,

                source_url:          window.location.href,
              }
            }
          }
        }),
      }
    );
    return res;
  }

  // async function fireDataLayer(data) {
  //   var results = await Promise.allSettled([sha256hex(data.email), sha256hex(data.phone)]);
  //   var payload = {
  //     event:        CONFIG.gtmEventName,
  //     email:        data.email,
  //     phone:        data.phone,
  //     hashedEmail:  results[0].status === 'fulfilled' ? results[0].value : '',
  //     hashedPhone:  results[1].status === 'fulfilled' ? results[1].value : '',
  //     facilityType: data.facilityType,
  //     projectStage: data.projectStage,
  //     growSize:     data.growSize,

  //     pageName:     data.pageName,
  //     pageUrl:      data.pageUrl,

  //     source_url:   window.location.href,
  //   };
  //   window.dataLayer = window.dataLayer || [];
  //   window.dataLayer.push(payload);
  // }
   async function fireDataLayer(data) {
    var results = await Promise.allSettled([sha256hex(data.email), sha256hex(data.phone)]);
    var payload = {
      event: CONFIG.gtmEventName,
      email: data.email,
      phone: data.phone,
      hashedEmail: results[0].status === 'fulfilled' ? results[0].value : '',
      hashedPhone: results[1].status === 'fulfilled' ? results[1].value : '',
      growSize: data.growSize,
      pageName: data.pageName,
      pageUrl: data.pageUrl,
      source_url: window.location.href
    };

    if (data.company) {
      payload.company = data.company;
    }

    if (data.facilityType) {
      payload.facilityType = data.facilityType;
    }

    if (data.projectStage) {
      payload.projectStage = data.projectStage;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  }

  function initForm(formId, containerId, thankYouId) {
    var form          = document.getElementById(formId);
    var formContainer = document.getElementById(containerId);
    var thankYou      = document.getElementById(thankYouId);
    if (!form || !formContainer || !thankYou) {
      if (form || formContainer || thankYou) {
      }
      return;
    }


    if (form.dataset.gtdKlaviyoBound === '1') return;
    form.dataset.gtdKlaviyoBound = '1';
    var lastAttempt = null, lastSignature = null, submitting = false;
    var submitBtn = form.querySelector('[type="submit"]');
    var btnText   = submitBtn ? submitBtn.querySelector('.consulting-btn-text') : null;

    function showThankYou() {
      formContainer.style.display = 'none';
      thankYou.style.display      = 'block';
      thankYou.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    form.addEventListener('submit', async function (e) {
      e.preventDefault();
      if (submitting) return;
      submitting = true;
      const priorButtonText = btnText?.textContent || 'Send';
      const previousError = form.querySelector('[data-gtd-delivery-error]');
      if (previousError) previousError.remove();
      if (submitBtn) submitBtn.disabled = true;
      if (btnText)   btnText.textContent = 'Sending…';

      // var v = function (sel) {
      //   var el = form.querySelector(sel);
      //   return el ? el.value : '';
      // };
      // var data = {
      //   name:         v('[name="contact[name]"]'),
      //   company:      v('[name="contact[Company]"]'),
      //   phone:        v('[name="contact[phone]"]'),
      //   email:        v('[name="contact[email]"]'),
      //   facilityType: v('[name="contact[Facility Type]"]'),
      //   projectStage: v('[name="contact[Project Stage]"]'),
      //   growSize:     v('[name="contact[Estimated Grow Size]"]'),

      //   pageName:     v('[name="contact[Page Name]"]'),
      //   pageUrl:      v('[name="contact[Page URL]"]'),
      // };

       function getFieldValue(form, fieldName) {
        var field = form.querySelector(
          '[name="' + fieldName + '"]'
        );

        return field ? field.value.trim() : '';
      }

      var data = {
        name: getFieldValue(form, 'contact[name]'),
        email: getFieldValue(form, 'contact[email]'),
        phone: getFieldValue(form, 'contact[phone]'),
        company: getFieldValue(form, 'contact[Company]'),
        facilityType: getFieldValue(form, 'contact[Facility Type]'),
        projectStage: getFieldValue(form, 'contact[Project Stage]'),
        growSize: getFieldValue(form, 'contact[Estimated Grow Size]'),
        pageName: getFieldValue(form, 'contact[Page Name]'),
        pageUrl: getFieldValue(form, 'contact[Page URL]')
      };

      const signature = JSON.stringify(data);
      if (lastAttempt && signature === lastSignature) data = lastAttempt;
      else {
        data.tracking = window.GTDInquiryTracking?.capture(form.id, 'consulting_request') || {};
        data.deliveryId = data.tracking.gtd_inquiry_id || crypto.randomUUID();
        data.submittedAt = new Date().toISOString();
        lastAttempt = data; lastSignature = signature;
      }
      var normalisedPhone = normalizePhone(data.phone);

      // 0. Klaviyo JS SDK identify — updates phone on the profile immediately, works for
      //    new AND existing subscribers (Subscriptions API silently no-ops for existing ones)
      if (window._learnq) {
        var n = splitName(data.name);
        var identifyPayload = { '$email': data.email, '$first_name': n.first, '$last_name': n.last, '$organization': data.company };
        if (normalisedPhone) identifyPayload['$phone_number'] = normalisedPhone;
        try {window._learnq.push(['identify', identifyPayload]);} catch (_) {}
      } else {
      }

      try {
        // Preview uses a real TEST event only; it does not subscribe customers or trigger live lead conversions.
        if (!isTrackingTest() && !data.subscriptionAccepted) {
          await subscribeToList(data); data.subscriptionAccepted = true;
        }
        await submitToKlaviyo(data);
        window.GTDKlaviyoFormTracking?.recordAccepted(buildProfileAttrs(data), data.tracking, data);
        if (!isTrackingTest()) {try {await fireDataLayer(data);} catch (_) {}}
        showThankYou();
        lastAttempt = null; lastSignature = null;
      } catch (_) {
        const error = document.createElement('p');
        error.dataset.gtdDeliveryError = '1'; error.setAttribute('role','alert');
        error.textContent = "We couldn't confirm your request was sent. Your information is still here. Please try again.";
        form.appendChild(error);
      } finally {
        submitting = false;
        if (submitBtn) submitBtn.disabled = false;
        if (btnText) btnText.textContent = priorButtonText;
      }
    });
  }

  function initAll() {
    initForm('consulting-hero-contact-form',   'consulting-hero-form-container',   'consulting-hero-thank-you');
    initForm('consulting-footer-contact-form', 'consulting-footer-form-container', 'consulting-footer-thank-you');
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  document.addEventListener('shopify:section:load', initAll);

})();
